import React from "react";
import styled from "styled-components";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

const DragHandleContext = React.createContext(null);

export const useDragHandle = () => {
  const context = React.useContext(DragHandleContext);
  return context || {};
};

const DraggableList = ({
  items = [],
  onReorder,
  renderItem,
  disabled = false,
}) => {
  const [activeId, setActiveId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder(newItems);
    }

    setActiveId(null);
  };

  if (disabled) {
    return (
      <ListWrapper>
        {items.map((item, index) => (
          <div key={item.id}>{renderItem(item, index, false)}</div>
        ))}
      </ListWrapper>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <ListWrapper>
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              item={item}
              index={index}
              renderItem={renderItem}
            />
          ))}
        </ListWrapper>
      </SortableContext>

      <DragOverlay>
        {activeId ? (
          <OverlayWrapper>
            {renderItem(
              items.find((item) => item.id === activeId),
              items.findIndex((item) => item.id === activeId),
              true
            )}
          </OverlayWrapper>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

const SortableItem = ({ id, item, index, renderItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragHandleProps = {
    ref: setActivatorNodeRef,
    ...listeners,
    ...attributes,
  };

  return (
    <DragHandleContext.Provider value={dragHandleProps}>
      <div ref={setNodeRef} style={style}>
        {renderItem(item, index, isDragging)}
      </div>
    </DragHandleContext.Provider>
  );
};

export default DraggableList;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OverlayWrapper = styled.div`
  opacity: 0.9;
  cursor: grabbing;
`;